/**
 * *Change delivery…* (docs/features/order-to-invoice/106 §9.4, approved by
 * Ben): change where one order line's goods go, after the order. Built from
 * the shared pieces only: the Dialog, the item page's `DeliverySection`
 * (opened on the line's deliveries, a row goods have gone to locked) and the
 * delivery card (with the delivery as it was, and "Keep the
 * agreed price").
 *
 * Every price, sentence and refusal is the catalogue's: the dialog reads the
 * change priced from the line's `change_delivery.preview_url` whenever the
 * person changes something, and *Save changes* sends the same choices with
 * the price each card showed. A price that moved since (409) says so beside
 * the card and the change is read again.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { deliverySelectionBody } from "@shared-core/data/delivery-selection-body";
import type { DeliverySelection } from "@shared-core/data/delivery-types";
import type {
  ChangeDeliveryView,
  DeliveryChangeDelivery,
  DeliveryChangePreview,
  OrderLineView,
  OrderView,
} from "@shared-core/data/order-to-invoice-types";
import type { CustomerDeliveryServiceLevel } from "@shared-core/data/customer-basket-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  basketFailure,
  type ChangedDeliveryPrice,
} from "@shared-core/domain/basket-presentation";
import {
  NO_PICK,
  isAgreedPriceUnfinished,
  staffDeliveryChoicesOf,
  type DeliveryPick,
} from "@shared-core/domain/checkout-delivery";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { DeliveryCard } from "../delivery/DeliveryCard";
import {
  DeliverySection,
  type DeliveryOpening,
} from "../delivery/DeliverySection";

/** One Save's request id: a retry with it changes nothing more. */
const requestId = newUuid;

/** "Was Acme Exeter · Next day · £9.80" (doc 106 §10). */
export function wasReading(
  before: NonNullable<DeliveryChangeDelivery["before"]>,
): string {
  const price =
    before.price_pence === null
      ? "Not yet priced"
      : formatQuotedPrice(before.price_pence);
  return `Was ${[before.destination_name, before.service_level_label, price]
    .filter(Boolean)
    .join(" · ")}`;
}

/** "Delivery £19.60 → £24.40"; a figure not yet priced reads so. */
export function deliveryTotalReading(preview: DeliveryChangePreview): string {
  const figure = (pence: number | null) =>
    pence === null ? "Not yet priced" : formatQuotedPrice(pence);
  return `Delivery ${figure(preview.delivery_before_pence)} → ${figure(preview.delivery_after_pence)}`;
}

/** The line's deliveries as the dialog opens on them (views' reading), each with what has gone to it. */
export function openingOf(view: ChangeDeliveryView): DeliveryOpening[] {
  return view.deliveries.map((d) => ({
    addressId: d.address_id,
    label: d.destination_name ?? d.address_id,
    postcode: d.postcode,
    quantity: d.quantity,
    goneQuantity: d.gone_quantity,
    lockedNote: d.locked_note,
  }));
}

type Read =
  | { kind: "reading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; preview: DeliveryChangePreview };

export function ChangeDeliveryDialog({
  order,
  line,
  view,
  onClose,
  onSaved,
}: {
  order: OrderView;
  line: OrderLineView;
  /** The line's `change_delivery`: the dialog is only offered where views put one. */
  view: ChangeDeliveryView;
  onClose: () => void;
  /** The order as it reads after the change. */
  onSaved: (next: OrderView) => void;
}) {
  const adapter = useDataAdapter();
  const [opening] = useState(() => openingOf(view));
  const [selection, setSelection] = useState<DeliverySelection | null>(null);
  const [picks, setPicks] = useState<Record<string, DeliveryPick>>({});
  const [read, setRead] = useState<Read>({ kind: "reading" });
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  // The server's "price changed" sentence, said until the next pick: the re-read it triggers must not clear it.
  const [pricesMoved, setPricesMoved] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(true);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [changes, setChanges] = useState<Record<string, ChangedDeliveryPrice>>(
    {},
  );
  const [submission] = useState(requestId);
  const seq = useRef(0);

  const preview = read.kind === "ready" ? read.preview : null;
  const points = preview?.collection_points ?? [];
  // The latest answer's collection points, for a read started from an older render's closure.
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const shown = (preview?.deliveries ?? []).flatMap((d) =>
    d.after ? [d.after] : [],
  );
  // Picks for deliveries the latest answer still has: a regrouped delivery's old pick is dropped.
  const livePicks = Object.fromEntries(
    Object.entries(picks).filter(([ref]) =>
      (preview?.deliveries ?? []).some((d) => d.delivery_ref === ref),
    ),
  );

  const readChange = useCallback(
    (next: DeliverySelection, nextPicks: Record<string, DeliveryPick>) => {
      const mine = ++seq.current;
      setIsReading(true);
      setRead((current) =>
        current.kind === "ready" ? current : { kind: "reading" },
      );
      adapter
        .previewDeliveryChange(view, {
          delivery: deliverySelectionBody(next),
          choices: staffDeliveryChoicesOf(
            nextPicks,
            [],
            pointsRef.current,
            "preview",
          ),
        })
        .then((answer) => {
          if (mine !== seq.current) return;
          setIsReading(false);
          setRead({ kind: "ready", preview: answer });
          setRowErrors({});
          setRowsError(null);
          setAddressError(null);
          setBanner(null);
        })
        .catch((err: unknown) => {
          if (mine !== seq.current) return;
          setIsReading(false);
          bindRefusal(err);
          setRead((current) =>
            current.kind === "ready"
              ? current
              : { kind: "failed", message: basketFailure(err).summary },
          );
        });
    },
    [adapter, view],
  );

  // The server's refusals bind to their input: a row, the rows, the address; anything else is the banner.
  const bindRefusal = (err: unknown) => {
    const rows: Record<number, string> = {};
    let rowsMessage: string | null = null;
    let address: string | null = null;
    const unbound: string[] = [];
    for (const e of extractFieldErrors(err)) {
      const row = /^delivery\.delivery_lines\.(\d+)\./.exec(e.field);
      if (row) rows[Number(row[1])] = e.message;
      else if (e.field === "delivery.delivery_lines") rowsMessage = e.message;
      else if (e.field === "delivery.delivery_address_id") address = e.message;
      else unbound.push(e.message);
    }
    setRowErrors(rows);
    setRowsError(rowsMessage);
    setAddressError(address);
    const bound = Object.keys(rows).length > 0 || rowsMessage || address;
    setBanner(
      unbound.length > 0
        ? unbound.join(" ")
        : bound
          ? null
          : basketFailure(err).summary,
    );
  };

  useEffect(() => {
    if (selection) readChange(selection, picks);
    // A pick reads again through `pick`; the selection reads here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const pick = (
    deliveryRef: string,
    patch: Partial<DeliveryPick>,
    options?: { preview?: boolean },
  ) => {
    const next = {
      ...picks,
      [deliveryRef]: { ...(picks[deliveryRef] ?? NO_PICK), ...patch },
    };
    setPicks(next);
    setChanges({});
    setPricesMoved(null);
    if (options?.preview !== false && selection) readChange(selection, next);
  };

  const save = async () => {
    if (!selection || !preview) return;
    setSaving(true);
    try {
      const next = await adapter.applyOrderCommand(order, {
        kind: "change_delivery",
        lineRef: line.line_ref,
        input: {
          delivery: deliverySelectionBody(selection),
          choices: staffDeliveryChoicesOf(livePicks, shown, points, "place"),
          request_id: submission,
        },
      });
      onSaved(next);
    } catch (err) {
      const refused = basketFailure(err);
      if (refused.changedDeliveries) {
        setChanges(
          Object.fromEntries(
            refused.changedDeliveries.map((c) => [c.deliveryRef, c]),
          ),
        );
        setPricesMoved(refused.summary);
        readChange(selection, livePicks);
      } else bindRefusal(err);
    } finally {
      setSaving(false);
    }
  };

  const needsAgreedPrice = (preview?.deliveries ?? []).some(
    (d) => d.needs_agreed_price,
  );
  const unfinished = Object.values(livePicks).some(isAgreedPriceUnfinished);
  const canSave =
    !!selection &&
    !!preview &&
    !isReading &&
    preview.has_changes &&
    !needsAgreedPrice &&
    !unfinished &&
    !saving;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Change delivery of {line.line_no} {line.name.title} ·{" "}
            {line.quantity.toLocaleString("en-GB")}
          </DialogTitle>
          <DialogDescription>
            Where it goes, and each delivery's service and price.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {(pricesMoved ?? banner) && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-400">
              {pricesMoved ?? banner}
            </p>
          )}
          <DeliverySection
            customerCode={order.customer_code}
            addressBook="customer_spine"
            productionSite=""
            allowedModes={
              line.versions.length > 1 ? ["ship"] : ["ship", "multi"]
            }
            quantity={line.quantity}
            opening={opening}
            destinationError={addressError}
            deliveryLinesError={rowsError}
            deliveryLineErrors={rowErrors}
            onChange={setSelection}
            disabled={saving}
          />
          <section aria-label="Deliveries" className="space-y-3">
            <h3 className="text-sm font-semibold">Deliveries</h3>
            {read.kind === "reading" && (
              <p role="status" className="text-sm text-muted-foreground">
                Pricing the deliveries…
              </p>
            )}
            {read.kind === "failed" && (
              <p
                role="alert"
                className="text-sm text-red-700 dark:text-red-400"
              >
                {read.message}
              </p>
            )}
            {preview && (
              <ul className="space-y-3">
                {preview.deliveries.map((d) => (
                  <ChangedDelivery
                    key={d.delivery_ref}
                    delivery={d}
                    points={points}
                    pick={picks[d.delivery_ref]}
                    change={changes[d.delivery_ref] ?? null}
                    disabled={saving}
                    onPick={pick}
                  />
                ))}
              </ul>
            )}
          </section>
          {view.supplier_notice && (
            <p className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              {view.supplier_notice}
            </p>
          )}
          {preview?.notices.map((notice) => (
            <p key={notice} className="text-sm text-muted-foreground">
              {notice}
            </p>
          ))}
        </div>
        <DialogFooter className="items-center gap-3 sm:justify-between">
          <p className="text-sm font-medium tabular-nums">
            {preview ? deliveryTotalReading(preview) : null}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Unticking "Keep the agreed price" re-prices it at its own level, else the first level it is offered. */
function repriceLevelOf(
  priced: NonNullable<DeliveryChangeDelivery["after"]>["delivery"],
): CustomerDeliveryServiceLevel | null {
  const levels = [
    priced.service_level,
    ...(priced.options ?? []).map((o) => o.service_level),
  ];
  return (
    levels.find(
      (level): level is CustomerDeliveryServiceLevel =>
        !!level && level !== "collection",
    ) ?? null
  );
}

/** One delivery the change touches, as the basket's card reads it, with what it was. */
export function ChangedDelivery({
  delivery,
  points,
  pick,
  change,
  disabled,
  onPick,
}: {
  delivery: DeliveryChangeDelivery;
  points: DeliveryChangePreview["collection_points"];
  pick: DeliveryPick | undefined;
  change: ChangedDeliveryPrice | null;
  disabled: boolean;
  onPick: (
    deliveryRef: string,
    patch: Partial<DeliveryPick>,
    options?: { preview?: boolean },
  ) => void;
}) {
  const before = delivery.before;
  if (delivery.kind === "removed" || !delivery.after)
    return (
      <li className="rounded-md border border-dashed border-border p-3 text-sm">
        {before && (
          <p
            className={
              delivery.kind === "removed"
                ? "text-muted-foreground line-through"
                : "text-muted-foreground"
            }
          >
            {wasReading(before)}
          </p>
        )}
        {delivery.note && <p>{delivery.note}</p>}
      </li>
    );
  const after = delivery.after;
  const isAgreed = after.delivery.source === "agreed";
  const keeps = pick?.serviceLevel == null && !pick?.isCollected;
  return (
    <DeliveryCard
      delivery={after}
      points={points}
      pick={pick}
      errors={{}}
      change={change}
      disabled={disabled}
      onPick={onPick}
      was={before ? wasReading(before) : null}
      keepAgreedPrice={
        before?.agreed_delivery_reason || (isAgreed && before)
          ? {
              isKept: isAgreed && keeps,
              onChange: (isKept) =>
                onPick(
                  delivery.delivery_ref,
                  isKept
                    ? {
                        serviceLevel: null,
                        courierSupplierId: null,
                        agreed: null,
                      }
                    : {
                        serviceLevel: repriceLevelOf(after.delivery),
                        courierSupplierId: null,
                        agreed: null,
                      },
                ),
            }
          : null
      }
    />
  );
}
